/**
 * Money.
 *
 * Every monetary value in this application is an integer number of paise.
 * There is no representation of rupees as a number anywhere outside this module's
 * display helpers, and no floating-point arithmetic in the money path (AD-03).
 *
 * Rounding happens in exactly one place: `roundHalfUp`. Nothing else in the codebase
 * may round a monetary value.
 */

export type Paise = number;

/** An exchange rate, scaled by 1e6. 83.254 INR per USD is 83_254_000. */
export type Ppm = number;

export const PAISE_PER_RUPEE = 100;
export const PPM_SCALE = 1_000_000;

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

// ---------------------------------------------------------------------------
// Rounding — the single documented point (AD-03)
// ---------------------------------------------------------------------------

/**
 * Round half away from zero.
 *
 * JavaScript's `Math.round` rounds half toward +Infinity, so `Math.round(-2.5)` is -2.
 * For money that is wrong: it makes rounding direction depend on sign, which means a
 * refund and a charge of the same magnitude round differently.
 */
export function roundHalfUp(value: number): number {
  if (!Number.isFinite(value)) {
    throw new MoneyError(`cannot round non-finite value: ${value}`);
  }
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/** Integer division of bigints, rounding half away from zero. */
export function divRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new MoneyError('division by zero');

  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;

  const quotient = n / d;
  const remainder = n % d;
  // Round up when the remainder is at least half the divisor.
  const rounded = remainder * 2n >= d ? quotient + 1n : quotient;

  return negative ? -rounded : rounded;
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

/**
 * Negate an amount without producing `-0`.
 *
 * `-0` is not a curiosity here. Firestore serialises it as a **double**, not an integer,
 * so a security rule asserting `amountPaise is int` rejects it — and the write fails with
 * "evaluation error", which looks nothing like the actual cause. It also renders as
 * "-0%" and makes `Object.is(-0, 0)` false.
 *
 * Any place that flips a sign should use this rather than a bare `-x`.
 */
export function negate(paise: Paise): Paise {
  return paise === 0 ? 0 : -paise;
}

export function isPaise(value: unknown): value is Paise {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

export function assertPaise(value: unknown, label = 'amount'): asserts value is Paise {
  if (!isPaise(value)) {
    throw new MoneyError(`${label} must be an integer number of paise, got ${String(value)}`);
  }
}

// ---------------------------------------------------------------------------
// Conversion in and out
// ---------------------------------------------------------------------------

/**
 * Parse user or imported text into paise.
 *
 * Parses the decimal string directly rather than going via `parseFloat`, so
 * "1234.56" cannot arrive as 1234.5599999999999. Accepts grouping separators and a
 * leading currency symbol. Returns null when the input is not a plain number —
 * arithmetic expressions are the expression module's job, not this one.
 */
export function parseAmount(input: string): Paise | null {
  const cleaned = input.trim().replace(/[₹$\s,]/g, '');
  if (cleaned === '') return null;

  const match = /^(-)?(\d*)(?:\.(\d*))?$/.exec(cleaned);
  if (!match) return null;

  const [, sign, whole = '', fraction = ''] = match;
  if (whole === '' && fraction === '') return null;

  const wholePaise = BigInt(whole === '' ? '0' : whole) * BigInt(PAISE_PER_RUPEE);

  // Take two decimal places, rounding on the third.
  const padded = fraction.padEnd(3, '0');
  const fractionPaise = divRoundHalfUp(BigInt(padded.slice(0, 3)), 10n);

  const total = wholePaise + fractionPaise;
  const signed = sign === '-' ? -total : total;

  if (signed > BigInt(Number.MAX_SAFE_INTEGER) || signed < BigInt(Number.MIN_SAFE_INTEGER)) {
    return null;
  }
  return Number(signed);
}

export function rupeesToPaise(rupees: number): Paise {
  if (!Number.isFinite(rupees)) throw new MoneyError(`not a finite number: ${rupees}`);
  return roundHalfUp(rupees * PAISE_PER_RUPEE);
}

/** For display and export only. Never feed the result back into a calculation. */
export function paiseToRupees(paise: Paise): number {
  assertPaise(paise);
  return paise / PAISE_PER_RUPEE;
}

// ---------------------------------------------------------------------------
// Currency conversion (FR-CUR-03)
// ---------------------------------------------------------------------------

/**
 * Convert an amount to INR using the rate frozen on the transaction.
 *
 * BigInt intermediates because paise x ppm overflows Number.MAX_SAFE_INTEGER at
 * around ₹92,000 — well inside this user's range, given a ₹10,23,938 car loan.
 */
export function convertToInr(amountPaise: Paise, ratePpm: Ppm): Paise {
  assertPaise(amountPaise);
  if (!Number.isSafeInteger(ratePpm) || ratePpm <= 0) {
    throw new MoneyError(`rate must be a positive scaled integer, got ${ratePpm}`);
  }
  const result = divRoundHalfUp(BigInt(amountPaise) * BigInt(ratePpm), BigInt(PPM_SCALE));
  return Number(result);
}

export function rateToPpm(rate: number): Ppm {
  if (!Number.isFinite(rate) || rate <= 0) {
    throw new MoneyError(`rate must be a positive finite number, got ${rate}`);
  }
  return roundHalfUp(rate * PPM_SCALE);
}

export function ppmToRate(ppm: Ppm): number {
  return ppm / PPM_SCALE;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export interface FormatOptions {
  /** Include the currency symbol. Default true. */
  symbol?: boolean;
  /** Always show a sign, even when positive. Default false. */
  signed?: boolean;
  /** Hide the decimal part when it is zero. Default false. */
  compact?: boolean;
  currency?: 'INR' | 'USD';
}

/**
 * Indian digit grouping — 12,11,659 not 1,211,659 — via the en-IN locale.
 * USD amounts group in the western style, since that is how a USD figure is read.
 */
export function formatPaise(paise: Paise, options: FormatOptions = {}): string {
  assertPaise(paise);
  const { symbol = true, signed = false, compact = false, currency = 'INR' } = options;

  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  const showDecimals = !compact || paise % PAISE_PER_RUPEE !== 0;

  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(Math.abs(paise) / PAISE_PER_RUPEE);

  const sign = paise < 0 ? '-' : signed ? '+' : '';
  const prefix = symbol ? (currency === 'INR' ? '₹' : '$') : '';

  return `${sign}${prefix}${formatted}`;
}
